/**
 * Shared algorithm utilities — pure functions, no React dependencies
 */

export const ALGORITHMS = {
  BOTTOM_LEFT: 'bottom-left',
  GUILLOTINE: 'guillotine',
  MAXIMAL_RECTANGLES: 'maximal-rectangles',
  SVGNEST: 'svgnest',
};

export const SHAPE_TYPES = {
  RECTANGLE: 'rectangle',
  TRAPEZOID: 'trapezoid',
  POLYGON: 'polygon',
};

export const SORT_STRATEGIES = {
  DESCSS: 'descss',
  AREA: 'area',
  PERIMETER: 'perimeter',
  LONG_SIDE: 'long_side',
  DIFFERENCE: 'difference',
};

/**
 * DESCSS (Descending Short Side) sorting - optimal preprocessing per Jylänki
 */
export const sortByDescendingShortSide = (pieces) => {
  return [...pieces].sort((a, b) => {
    const aShort = Math.min(a.width, a.height);
    const bShort = Math.min(b.width, b.height);
    const aLong = Math.max(a.width, a.height);
    const bLong = Math.max(b.width, b.height);
    if (bShort !== aShort) return bShort - aShort;
    if (bLong !== aLong) return bLong - aLong;
    if (a.grainDirection !== 'none' && b.grainDirection === 'none') return -1;
    if (a.grainDirection === 'none' && b.grainDirection !== 'none') return 1;
    return 0;
  });
};

/** Sort by descending area */
export const sortByDescendingArea = (pieces) => {
  return [...pieces].sort((a, b) => (b.width * b.height) - (a.width * a.height));
};

/** Sort by descending perimeter */
export const sortByDescendingPerimeter = (pieces) => {
  return [...pieces].sort((a, b) => (2*(b.width + b.height)) - (2*(a.width + a.height)));
};

/** Sort by descending long side */
export const sortByDescendingLongSide = (pieces) => {
  return [...pieces].sort((a, b) => Math.max(b.width, b.height) - Math.max(a.width, a.height));
};

/** Sort by descending difference |w-h| (squarest pieces last) */
export const sortByDescendingDifference = (pieces) => {
  return [...pieces].sort((a, b) => Math.abs(b.width - b.height) - Math.abs(a.width - a.height));
};

/** Apply a sort strategy by key */
export const applySortStrategy = (pieces, strategy) => {
  switch (strategy) {
    case SORT_STRATEGIES.AREA: return sortByDescendingArea(pieces);
    case SORT_STRATEGIES.PERIMETER: return sortByDescendingPerimeter(pieces);
    case SORT_STRATEGIES.LONG_SIDE: return sortByDescendingLongSide(pieces);
    case SORT_STRATEGIES.DIFFERENCE: return sortByDescendingDifference(pieces);
    case SORT_STRATEGIES.DESCSS:
    default:
      return sortByDescendingShortSide(pieces);
  }
};

/**
 * Gets possible orientations for a piece based on grain direction
 */
export const getPossibleOrientations = (piece) => {
  const orientations = [];
  orientations.push({ ...piece, rotated: false });

  if (piece.grainDirection === 'none' && piece.width !== piece.height) {
    orientations.push({ ...piece, width: piece.height, height: piece.width, rotated: true });
  } else if (piece.grainDirection === 'horizontal' && piece.width < piece.height) {
    orientations.splice(0, 1);
    orientations.push({ ...piece, width: piece.height, height: piece.width, rotated: true });
  } else if (piece.grainDirection === 'vertical' && piece.width > piece.height) {
    orientations.splice(0, 1);
    orientations.push({ ...piece, width: piece.height, height: piece.width, rotated: true });
  }

  return orientations;
};

/**
 * Spatial grid for O(1) amortized collision detection.
 */
export class SpatialGrid {
  constructor(sheetWidth, sheetHeight, cellSize) {
    this.cellSize = cellSize || Math.max(1, Math.min(sheetWidth, sheetHeight) * 0.1);
    this.cols = Math.ceil(sheetWidth / this.cellSize) + 1;
    this.rows = Math.ceil(sheetHeight / this.cellSize) + 1;
    this.cells = new Array(this.cols * this.rows).fill(null);
  }

  _key(col, row) {
    return row * this.cols + col;
  }

  insert(rect) {
    const cs = this.cellSize;
    const c0 = Math.max(0, Math.floor(rect.x / cs));
    const c1 = Math.min(this.cols - 1, Math.floor((rect.x + rect.width) / cs));
    const r0 = Math.max(0, Math.floor(rect.y / cs));
    const r1 = Math.min(this.rows - 1, Math.floor((rect.y + rect.height) / cs));
    for (let r = r0; r <= r1; r++) {
      for (let c = c0; c <= c1; c++) {
        const k = this._key(c, r);
        if (this.cells[k] === null) this.cells[k] = [];
        this.cells[k].push(rect);
      }
    }
  }

  query(x, y, w, h) {
    const cs = this.cellSize;
    const c0 = Math.max(0, Math.floor(x / cs));
    const c1 = Math.min(this.cols - 1, Math.floor((x + w) / cs));
    const r0 = Math.max(0, Math.floor(y / cs));
    const r1 = Math.min(this.rows - 1, Math.floor((y + h) / cs));
    const seen = new Set();
    const results = [];
    for (let r = r0; r <= r1; r++) {
      for (let c = c0; c <= c1; c++) {
        const bucket = this.cells[this._key(c, r)];
        if (bucket) {
          for (const rect of bucket) {
            if (!seen.has(rect)) {
              seen.add(rect);
              results.push(rect);
            }
          }
        }
      }
    }
    return results;
  }

  hasCollision(x, y, w, h, cutW) {
    const candidates = this.query(x - cutW, y - cutW, w + 2 * cutW, h + 2 * cutW);
    for (const rect of candidates) {
      if (!(x >= rect.x + rect.width + cutW ||
            x + w + cutW <= rect.x ||
            y >= rect.y + rect.height + cutW ||
            y + h + cutW <= rect.y)) {
        return true;
      }
    }
    return false;
  }
}

/**
 * Check if a piece can be placed at a given position.
 * Accepts optional SpatialGrid for O(1) collision checks.
 */
export const canPlaceAt = (piece, x, y, placedRectangles, sheetW, sheetH, cutW, grid) => {
  if (x + piece.width > sheetW || y + piece.height > sheetH) return false;
  if (grid) {
    return !grid.hasCollision(x, y, piece.width, piece.height, cutW);
  }
  for (const rect of placedRectangles) {
    if (!(x >= rect.x + rect.width + cutW ||
          x + piece.width + cutW <= rect.x ||
          y >= rect.y + rect.height + cutW ||
          y + piece.height + cutW <= rect.y)) {
      return false;
    }
  }
  return true;
};

/**
 * Find the best position for a piece using bottom-left algorithm.
 * Accepts optional SpatialGrid for O(1) collision checks.
 */
export const findBestPosition = (piece, placedRectangles, sheetW, sheetH, cutW, grid) => {
  const positions = [{ x: 0, y: 0 }];
  placedRectangles.forEach(rect => {
    positions.push({ x: rect.x + rect.width + cutW, y: rect.y });
    positions.push({ x: rect.x, y: rect.y + rect.height + cutW });
  });
  positions.sort((a, b) => a.y - b.y || a.x - b.x);
  for (const pos of positions) {
    if (canPlaceAt(piece, pos.x, pos.y, placedRectangles, sheetW, sheetH, cutW, grid)) {
      return pos;
    }
  }
  return null;
};

/**
 * Score a packing result for comparison.
 * Returns { sheets, eff, unplaced } — lower unplaced > fewer sheets > higher eff.
 */
export const scoreResult = (result, sheetArea) => {
  if (!result || !result.layouts) return { sheets: Infinity, eff: 0, unplaced: Infinity };
  const sheets = result.layouts.length;
  const placedArea = result.layouts.reduce((s, l) =>
    s + l.pieces.reduce((s2, p) => s2 + p.width * p.height, 0), 0);
  const usedSheetArea = sheets * sheetArea;
  const eff = usedSheetArea > 0 ? placedArea / usedSheetArea : 0;
  return { sheets, eff, unplaced: result.unplacedCount || 0 };
};

/**
 * Compare two scores: returns true if `a` is strictly better than `b`.
 */
export const isBetter = (a, b) => {
  if (a.unplaced !== b.unplaced) return a.unplaced < b.unplaced;
  if (a.sheets !== b.sheets) return a.sheets < b.sheets;
  return a.eff > b.eff;
};

/**
 * Cost function for SA: lower is better.
 * Uses scoreResult internally, then converts to a single scalar.
 */
export const scoreToCost = (result, sheetArea) => {
  if (!result || !result.layouts) return Infinity;
  const s = scoreResult(result, sheetArea);
  return s.unplaced * 10000 + s.sheets * 100 + (1 - s.eff) * 50;
};

/**
 * Remove rectangles fully contained within others.
 * Optimized: sort by area descending so large rects are checked first as
 * containers, and smaller rects can skip comparisons where area makes
 * containment impossible.
 */
export const pruneContainedRects = (rects) => {
  const EPS = 0.001;
  if (rects.length <= 1) return rects;

  const indexed = rects.map((r, i) => ({
    ...r,
    area: r.width * r.height,
    origIdx: i,
  }));
  indexed.sort((a, b) => b.area - a.area);

  const removed = new Set();

  for (let i = 0; i < indexed.length; i++) {
    if (removed.has(i)) continue;
    const candidate = indexed[i];

    for (let j = i + 1; j < indexed.length; j++) {
      if (removed.has(j)) continue;
      const smaller = indexed[j];

      if (smaller.area > candidate.area + EPS) continue;

      if (smaller.x >= candidate.x - EPS && smaller.y >= candidate.y - EPS &&
          smaller.x + smaller.width <= candidate.x + candidate.width + EPS &&
          smaller.y + smaller.height <= candidate.y + candidate.height + EPS) {
        removed.add(j);
      }
    }
  }

  return indexed.filter((_, i) => !removed.has(i)).map(({ area, origIdx, ...r }) => r);
};
